import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { apiGet, getApiErrorMessage, type ApiResult } from "@/api";
import { tokenQuery } from "./query-options";

export interface BillingData {
  /** The Stripe subscription THIS user personally holds. Only their own row can be managed. */
  planId: string;
  /**
   * What the user may actually DO — the same authority every API gate uses, so an inherited
   * team seat or a dead `unpaid` subscription is reflected here and nowhere else. Optional
   * because a client can outlive an API that predates the field; the caller falls back to
   * `planId`, which is the pre-existing behaviour.
   */
  effectivePlanId?: string;
  /** Raw Stripe state — this is what "past_due, update your card" is read from. */
  status: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
}

const defaultBilling: BillingData = {
  planId: "free",
  effectivePlanId: "free",
  status: "none",
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  currentPeriodEnd: null,
};

/**
 * Turns a `/billing/status` response into what the billing page may offer, or throws.
 *
 * Separated from the query to be testable; it knows nothing about React or the query cache.
 *
 * The default is the interesting half. It is what a user with no Stripe row is shown, and it
 * must be the LEAST entitled plan: read as `team`, a Free user is presented as a Team customer,
 * offered "downgrade" where "upgrade" belongs, and handed seat management for a subscription
 * they do not have. Failing open here is a billing decision, not a display one — which is why
 * the test states `free` literally rather than importing this constant to compare against
 * itself.
 */
export function selectBillingStatus(res: ApiResult<{ billing?: BillingData }>): BillingData {
  if (!res.ok) throw new Error(getApiErrorMessage(res, "Billing-Status konnte nicht geladen werden"));
  return res.data.billing ?? defaultBilling;
}

export function billingStatusQueryOptions(token: string | null) {
  return tokenQuery(["billing-status"], token, async (t) =>
    selectBillingStatus(await apiGet<{ billing?: BillingData }>("/billing/status", t)),
  );
}

export function useBillingStatus() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(billingStatusQueryOptions(token));
}
